import React, { useCallback } from "react"
import { Box, Text, Flex, Heading, AspectRatio, Image, Button } from "@chakra-ui/react"
import { useShopify, useShopifyPrice, useShopifyProductLive, useShopifyProductRaw } from "@app/hooks/useShopify"
import { useCart } from "@app/hooks/useCart"
import Link from "@app/components/Link"
import { useRoutes } from "@app/hooks/useRoutes"
import { useConfigContext } from "@app/providers/config"
import { useProductComputed } from "@app/hooks/useProductComputed"

type Props = {
  productGiftSet: GatsbyTypes.SanityProduct["giftSet"]
}

const ProductTabGiftSet: React.FC<Props> = ({ productGiftSet }) => {
  return (
    <Box py={{ base: 0, lg: "32px" }}>
      <Flex
        gap={{ base: "16px", lg: "16px" }}
        flexDir={{ base: "column", lg: "row" }}
        background={{ base: "primary.purpleLight", lg: "primary.white" }}
      >
        {productGiftSet?.items?.map(item => <ProductTabGiftSetItem key={item.id} item={item} />)}
      </Flex>
    </Box>
  )
}

type ItemProps = {
  item: GatsbyTypes.SanityProduct["giftSet"]["items"]
}

const ProductTabGiftSetItem: React.FC<ItemProps> = ({ item }) => {
  const { imageNormaliser } = useShopify()
  const image = imageNormaliser(item?.image, 1200)
  const { product: rawProduct } = useShopifyProductRaw(item)
  const { product: liveProduct, isUnavailable } = useShopifyProductLive(rawProduct)
  const { urlResolver } = useRoutes()
  const {
    settings: { routes },
  } = useConfigContext()

  const product = liveProduct || rawProduct

  const productLink = urlResolver(product, routes.PRODUCT)

  const { addToCart, loading: cartLoading } = useCart()

  const firstAvailableVariant = product?.variants?.find(variant => variant.availableForSale)

  const { formattedPrice } = useShopifyPrice(firstAvailableVariant, 1)

  const { productSizeText } = useProductComputed(product)

  const handleClick = useCallback(async () => {
    if (firstAvailableVariant && !isUnavailable && !cartLoading) {
      await addToCart(product, firstAvailableVariant, 1)
    }
  }, [firstAvailableVariant, isUnavailable, cartLoading, addToCart, product])

  return (
    <Flex borderRadius="2px" background={"primary.white"} py="16px" px={{ base: "4px", lg: "16px" }} gap="12px" flex={1}>
      <Box w="120px" as={Link} to={productLink.url}>
        <AspectRatio ratio={4 / 5} w="120px">
          <Image src={image?.src} objectFit="cover !important" w="120px" />
        </AspectRatio>
      </Box>
      <Flex flexDir="column" gap="4px">
        <Heading variant="heading16" fontWeight="bold">
          {product.title}
        </Heading>
        <Text lineHeight={1.5}>{productSizeText}</Text>
        <Text lineHeight={1.5} mb="12px">
          {product.description}
        </Text>
        <Button
          variant="outline"
          mt="auto"
          w="180px"
          h="40px"
          disabled={isUnavailable || !firstAvailableVariant}
          onClick={handleClick}
          isLoading={cartLoading}
        >
          {isUnavailable ? "Out of stock" : `Add ${formattedPrice}`}
        </Button>
      </Flex>
    </Flex>
  )
}

export default React.memo(ProductTabGiftSet)
